/* Cart-local rating dialog and Shopify recommendations. Verified retry 2026-09-23. No administrative tokens. */
(() => {
  'use strict';
  if (window.__szhCartEnhancements) return;
  const shared = window.__szhCartEnhancements = { ratings: new Map(), adding: false };
  const SIX_HOURS = 21600000;
  const root = () => window.Shopify?.routes?.root || '/';
  const moneyText = (value) => Number(value).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const dateText = (value) => new Date(value).toLocaleDateString('pl-PL');
  const fresh = (time, age) => Number.isFinite(time) && Date.now() >= time && Date.now() - time < age;

  async function fetchRating(tsid, host) {
    if (!/^X[A-F0-9]{32}$/.test(tsid)) return null;
    const key = `szh-cart-rating-v1-${tsid}`;
    const previous = shared.ratings.get(key);
    if (previous && fresh(previous.time, previous.failed ? 300000 : SIX_HOURS)) return previous.promise;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached?.host === host && cached.score >= 1 && cached.score <= 5 && fresh(cached.time, SIX_HOURS)) return cached;
    } catch (_) { /* Storage may be unavailable. */ }
    const entry = { time: Date.now(), failed: false };
    entry.promise = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 6000);
      try {
        const response = await fetch(`https://widgets.trustedshops.com/js/${tsid}-jsonld.json`, {
          credentials: 'omit', referrerPolicy: 'no-referrer', signal: controller.signal
        });
        if (!response.ok) throw new Error('Rating feed unavailable');
        const payload = await response.json();
        const entities = Array.isArray(payload) ? payload : (payload['@graph'] || [payload]);
        const entity = entities.find((item) => {
          if (!item?.aggregateRating) return false;
          try { return new URL(item.url || item['@id']).hostname.replace(/^www\./, '') === host; }
          catch (_) { return false; }
        });
        const score = Number(String(entity?.aggregateRating?.ratingValue || '').replace(',', '.'));
        const scale = Number(entity?.aggregateRating?.bestRating || 5);
        if (!Number.isFinite(score) || score < 1 || score > 5 || scale !== 5) throw new Error('Invalid rating');
        // ratingCount is deliberately not relabelled as the all-time review count.
        const data = { score, time: Date.now(), host };
        try { sessionStorage.setItem(key, JSON.stringify(data)); } catch (_) {}
        return data;
      } catch (_) {
        entry.failed = true;
        return null;
      } finally { clearTimeout(timeout); }
    })();
    shared.ratings.set(key, entry);
    return entry.promise;
  }

  class SzhCartRating extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('cart-drawer');
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-rating-open]');
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-rating-open]')) this.openDialog();
        if (event.target.closest('[data-rating-close]')) this.closeDialog();
        if (event.target === this.dialog) {
          const box = this.dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.closeDialog();
        }
      }, options);
      this.addEventListener('keydown', (event) => {
        if (!this.dialog?.open) return;
        if (event.key === 'Escape') event.stopPropagation();
        if (event.key === 'Tab') {
          const buttons = [...this.dialog.querySelectorAll('button:not([disabled])')];
          const first = buttons[0];
          const last = buttons[buttons.length - 1];
          if ((event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
            event.preventDefault();
            event.stopPropagation();
            (event.shiftKey ? last : first)?.focus();
          }
        }
      }, options);
      this.addEventListener('keyup', (event) => {
        if (this.dialog?.open && event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.closeDialog();
        }
      }, options);
      // Wait for keyup so the parent cart's Escape handler cannot close the cart.
      this.dialog?.addEventListener('cancel', (event) => event.preventDefault(), options);
      this.dialog?.addEventListener('close', () => this.restoreFocus(), options);
      this.observer = new MutationObserver(() => {
        if (this.drawer?.classList.contains('active')) this.refresh();
        else if (this.dialog?.open) this.closeDialog();
      });
      if (this.drawer) this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refresh(); }, options);
      this.paint(null);
      if (this.drawer?.classList.contains('active')) this.refresh();
    }
    paint(data) {
      const snapshotTime = Date.parse(this.dataset.snapshotDate);
      const snapshotExpiry = Number(this.dataset.snapshotExpires);
      const validSnapshot = Number.isFinite(snapshotTime) && Date.now() >= snapshotTime && Date.now() < snapshotExpiry;
      const validLive = data && Number.isFinite(data.score) && data.score >= 1 && data.score <= 5 && fresh(data.time, SIX_HOURS);
      data = validLive ? data : null;
      this.lastRating = data;
      const fallback = Number(this.dataset.snapshotScore);
      const score = data?.score || (validSnapshot && fallback >= 1 && fallback <= 5 ? fallback : null);
      this.querySelectorAll('[data-rating-values]').forEach((node) => { node.hidden = !score; });
      this.querySelectorAll('[data-rating-unavailable]').forEach((node) => { node.hidden = Boolean(score); });
      this.querySelectorAll('[data-rating-total]').forEach((node) => { node.hidden = !validSnapshot; });
      if (score) {
        this.querySelectorAll('[data-rating-score]').forEach((node) => { node.textContent = `${moneyText(score)} / 5`; });
        this.querySelectorAll('[data-rating-stars]').forEach((row) => {
          [...row.children].forEach((star, index) => star.style.setProperty('--fill', `${Math.max(0, Math.min(1, score - index)) * 100}%`));
        });
      }
      const stamp = this.querySelector('[data-rating-updated]');
      if (stamp) {
        stamp.hidden = !score;
        stamp.textContent = data ? `Dane oceny pobrane ${dateText(data.time)} r.` : `Ocena potwierdzona ${dateText(snapshotTime)} r.`;
      }
      clearTimeout(this.expiryTimeout);
      // The dated all-time count must expire independently of a refreshed score.
      const deadlines = [];
      if (data) deadlines.push(data.time + SIX_HOURS);
      if (validSnapshot) deadlines.push(snapshotExpiry);
      const deadline = Math.min(...deadlines);
      if (Number.isFinite(deadline) && deadline > Date.now()) this.expiryTimeout = setTimeout(() => this.refresh(), Math.min(deadline - Date.now() + 50, 2147483647));
    }
    async refresh() {
      if (!this.isConnected || this.refreshing) return;
      this.refreshing = true;
      this.paint(this.lastRating);
      const data = await fetchRating(this.dataset.tsid, this.dataset.shopHost);
      if (this.isConnected) this.paint(data);
      this.refreshing = false;
    }
    openDialog() {
      if (!this.dialog || this.dialog.open) return;
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else {
        this.dialog.setAttribute('open', '');
        if (typeof trapFocus === 'function') trapFocus(this.dialog, this.dialog.querySelector('[data-rating-close]'));
      }
      this.opener?.setAttribute('aria-expanded', 'true');
      this.dialog.querySelector('[data-rating-close]')?.focus({ preventScroll: true });
      this.refresh();
    }
    closeDialog() {
      if (!this.dialog?.open) return;
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else { this.dialog.removeAttribute('open'); this.restoreFocus(); }
    }
    restoreFocus() {
      this.opener?.setAttribute('aria-expanded', 'false');
      if (!this.isConnected || !this.drawer?.classList.contains('active')) return;
      if (typeof trapFocus === 'function') trapFocus(this.drawer.querySelector('.drawer__inner'), this.opener);
      this.opener?.focus({ preventScroll: true });
    }
    disconnectedCallback() {
      this.listeners?.abort();
      this.observer?.disconnect();
      clearTimeout(this.expiryTimeout);
    }
  }

  class SzhCartRecommendations extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('cart-drawer');
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.addEventListener('change', (event) => {
        // The parent theme treats every change as a cart quantity change.
        event.stopPropagation();
        if (!event.target.matches('[data-reco-variant]')) return;
        const option = event.target.selectedOptions[0];
        const form = event.target.closest('form');
        form.querySelector('[name="quantity"]').value = option.dataset.quantity;
        form.querySelector('[data-reco-price]').textContent = option.dataset.price;
        const quantity = Number(option.dataset.quantity);
        form.querySelector('[data-reco-add-label]').textContent = quantity > 1 ? `Dodaj ${quantity} szt.` : 'Dodaj';
      }, options);
      this.addEventListener('submit', (event) => {
        if (!event.target.matches('[data-reco-form]')) return;
        event.preventDefault();
        event.stopPropagation();
        this.add(event.target);
      }, options);
      this.observer = new MutationObserver(() => this.maybeLoad());
      if (this.drawer) this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      this.inView = !('IntersectionObserver' in window);
      if ('IntersectionObserver' in window) {
        this.intersection = new IntersectionObserver((entries) => {
          this.inView = entries.some((entry) => entry.isIntersecting);
          this.maybeLoad();
        }, { root: this.closest('cart-drawer-items'), rootMargin: '150px 0px' });
        this.intersection.observe(this);
      }
      this.maybeLoad();
    }
    async maybeLoad() {
      if (!this.inView || this.loaded || this.loading || !this.drawer?.classList.contains('active')) return;
      if (this.failedAt && Date.now() - this.failedAt < 60000) return;
      this.loading = true;
      this.request = new AbortController();
      const timeout = setTimeout(() => this.request.abort(), 8000);
      try {
        const url = new URL(this.dataset.url, location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid recommendation origin');
        const response = await fetch(url, { credentials: 'same-origin', signal: this.request.signal });
        if (!response.ok) throw new Error('Recommendations unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = html.querySelector('[data-szh-recommendations-response]');
        if (!source) throw new Error('Invalid recommendation section');
        const excluded = new Set(JSON.parse(this.dataset.cartProducts || '[]').map(String));
        const seen = new Set();
        [...source.querySelectorAll('[data-reco-product]')].forEach((card) => {
          const id = card.dataset.recoProduct;
          if (!id || excluded.has(id) || seen.has(id)) card.remove();
          else seen.add(id);
        });
        [...source.querySelectorAll('[data-reco-product]')].slice(3).forEach((card) => card.remove());
        if (!this.isConnected) return;
        this.innerHTML = source.querySelector('[data-reco-product]') ? source.innerHTML : '';
        this.loaded = true;
        this.intersection?.disconnect();
      } catch (error) {
        if (this.isConnected) this.failedAt = Date.now();
      } finally { clearTimeout(timeout); this.loading = false; }
    }
    async add(form) {
      if (shared.adding || this.drawer?.querySelector('.cart__items--disabled')) return;
      const error = form.querySelector('[data-reco-error]');
      const showError = (text) => { if (error?.isConnected) { error.textContent = text; error.hidden = false; } };
      if (!this.drawer || typeof this.drawer.renderContents !== 'function') { showError('Koszyk jest jeszcze wczytywany. Spr\u00f3buj ponownie za chwil\u0119.'); return; }
      const id = Number(new FormData(form).get('id'));
      const quantity = Number(new FormData(form).get('quantity'));
      if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(quantity) || quantity < 1) return;
      shared.adding = true;
      if (error) error.hidden = true;
      const drawer = this.drawer;
      const scroll = drawer.querySelector('cart-drawer-items')?.scrollTop || 0;
      const accepted = Boolean(drawer.querySelector('#cart-drawer-terms')?.checked);
      const controls = [...drawer.querySelectorAll('#CartDrawer-Form input, #CartDrawer-Form button, szh-cart-recommendations button, szh-cart-recommendations select, #CartDrawer-Checkout, #cart-drawer-terms')].map((node) => [node, node.disabled]);
      controls.forEach(([node]) => { node.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      form.querySelector('[data-reco-add-label]').textContent = 'Dodawanie...';
      let added = false;
      let rejectedMessage = '';
      const addRequest = new AbortController();
      // No automatic POST retry: a missing response does not prove the add failed.
      const addTimeout = setTimeout(() => addRequest.abort(), 45000);
      try {
        const mainCart = document.querySelector('cart-items');
        const sections = [...new Set([
          ...drawer.getSectionsToRender().map((section) => section.id),
          ...(mainCart?.getSectionsToRender?.() || []).map((section) => section.section)
        ])];
        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST', credentials: 'same-origin', signal: addRequest.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity }], sections, sections_url: location.pathname + location.search })
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          if (response.status >= 400 && response.status < 500) rejectedMessage = typeof result.description === 'string' ? result.description : 'Nie uda\u0142o si\u0119 doda\u0107 produktu. Sprawd\u017a dost\u0119pno\u015b\u0107 i spr\u00f3buj ponownie.';
          throw new Error('Add request failed');
        }
        added = true;
        const cartResponse = await fetch(`${root()}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: addRequest.signal });
        if (!cartResponse.ok) throw new Error('Cart refresh failed');
        const state = await cartResponse.json();
        let rendered = result.sections;
        if (!rendered || sections.some((name) => typeof rendered[name] !== 'string')) {
          const url = new URL(location.href);
          url.searchParams.set('sections', sections.join(','));
          const sectionResponse = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: addRequest.signal });
          if (!sectionResponse.ok) throw new Error('Cart sections unavailable');
          rendered = await sectionResponse.json();
        }
        if (sections.some((name) => typeof rendered[name] !== 'string')) throw new Error('Missing cart section');
        state.sections = rendered;
        if (mainCart?.isConnected) mainCart.renderContents(state);
        drawer.renderContents(state, true);
        drawer.classList.remove('is-empty');
        const terms = drawer.querySelector('#cart-drawer-terms');
        if (terms) terms.checked = accepted;
        const scroller = drawer.querySelector('cart-drawer-items');
        if (scroller) scroller.scrollTop = scroll;
        const live = drawer.querySelector('#CartDrawer-LiveRegionText');
        if (live) live.textContent = 'Dodano produkt do koszyka.';
        if (drawer.classList.contains('active')) {
          const inner = drawer.querySelector('.drawer__inner');
          if (typeof trapFocus === 'function') trapFocus(inner, inner);
          inner?.focus({ preventScroll: true });
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state, variantId: id, productVariantId: id });
      } catch (failure) {
        const text = added ? 'Produkt zosta\u0142 dodany. Od\u015bwie\u017c koszyk, aby zobaczy\u0107 aktualne podsumowanie.' : (rejectedMessage || 'Nie uda\u0142o si\u0119 potwierdzi\u0107 dodania produktu. Od\u015bwie\u017c koszyk przed ponown\u0105 pr\u00f3b\u0105.');
        showError(text);
        if (added) {
          const alert = drawer.querySelector('#CartDrawer-CartErrors');
          if (alert) alert.textContent = text;
        }
      } finally {
        clearTimeout(addTimeout);
        shared.adding = false;
        controls.forEach(([node, disabled]) => { if (node.isConnected) node.disabled = disabled; });
        this.removeAttribute('aria-busy');
        const label = form.querySelector('[data-reco-add-label]');
        if (label) label.textContent = quantity > 1 ? `Dodaj ${quantity} szt.` : 'Dodaj';
      }
    }
    disconnectedCallback() {
      this.listeners?.abort();
      this.observer?.disconnect();
      this.intersection?.disconnect();
      this.request?.abort();
    }
  }

  if (!customElements.get('szh-cart-rating')) customElements.define('szh-cart-rating', SzhCartRating);
  if (!customElements.get('szh-cart-recommendations')) customElements.define('szh-cart-recommendations', SzhCartRecommendations);
})();
